// adminportal/src/pages/UserManagement.tsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  User as UserIcon,
  Mail,
  Phone,
  Building2,
  Server,
  Shield,
} from 'lucide-react';
import Card from '../components/Card';
import Button from '../components/Button';
import { apiUrl } from '../config';
import Copyright from '../components/Copyright';

interface User {
  userId: string;
  username: string;
  email: string;
  phone?: string;
  companyId?: string;
  companyName?: string;
  role: string;
  status: 'active' | 'inactive' | 'pending';
  createdAt: string;
  lastLogin?: string;
  isServiceAccount?: boolean;
}

export default function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [serviceAccounts, setServiceAccounts] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [message, setMessage] = useState('');
  const [deleteModal, setDeleteModal] = useState<{
    show: boolean;
    user: User | null;
  }>({ show: false, user: null });
  const [roleModal, setRoleModal] = useState<{
    show: boolean;
    user: User | null;
    newRole: string;
  }>({ show: false, user: null, newRole: '' });

  // Load users on component mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Clear search results when search term is cleared
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredUsers(users);
    }
  }, [searchTerm, users]);

  const loadUsers = async () => {
    setLoading(true);
    setMessage('');

    try {
      const res = await fetch(apiUrl('users'));
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      if (data.success && data.users) {
        // Separate regular users and service accounts
        const regularUsers = data.users.filter((user: User) => !user.isServiceAccount);
        const serviceAccountsData = data.users.filter((user: User) => user.isServiceAccount);

        setUsers(regularUsers);
        setFilteredUsers(regularUsers);
        setServiceAccounts(serviceAccountsData);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to load users: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
      // Set empty arrays on error
      setUsers([]);
      setFilteredUsers([]);
      setServiceAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      await loadUsers();
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(apiUrl(`users/search?q=${encodeURIComponent(searchTerm)}`));
      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      if (data.success && data.users) {
        setFilteredUsers(data.users);
      } else {
        setFilteredUsers([]);
      }
    } catch {
      // Search error occurred
      setFilteredUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!deleteModal.user) return;

    setLoading(true);
    setMessage('');

    try {
      const res = await fetch(apiUrl(`users/${deleteModal.user.userId}`), {
        method: 'DELETE',
      });

      if (!res.ok) throw new Error(await res.text());

      setMessage(`✅ User "${deleteModal.user.username}" deleted successfully`);
      setDeleteModal({ show: false, user: null });

      // Reload users
      await loadUsers();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to delete user: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const closeDeleteModal = () => {
    setDeleteModal({ show: false, user: null });
  };

  const handleRoleUpdate = async () => {
    if (!roleModal.user || !roleModal.newRole) return;

    setLoading(true);
    setMessage('');

    try {
      const res = await fetch(apiUrl(`users/${roleModal.user.userId}/role`), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: roleModal.user.username,
          newRole: roleModal.newRole,
        }),
      });

      if (!res.ok) throw new Error(await res.text());

      const data = await res.json();
      setMessage(
        `✅ User "${roleModal.user.username}" role updated to ${roleModal.newRole} successfully`
      );
      setRoleModal({ show: false, user: null, newRole: '' });

      // Reload users
      await loadUsers();
    } catch (err: unknown) {
      setMessage(
        `❌ Failed to update user role: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    } finally {
      setLoading(false);
    }
  };

  const openRoleModal = (user: User) => {
    setRoleModal({ show: true, user, newRole: user.role });
  };

  const closeRoleModal = () => {
    setRoleModal({ show: false, user: null, newRole: '' });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-emerald-500/20 text-emerald-400';
      case 'inactive':
        return 'bg-slate-500/20 text-slate-400';
      case 'pending':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getRoleColor = (role: string) => {
    switch (role.toLowerCase()) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400';
      case 'user':
        return 'bg-blue-500/20 text-blue-400';
      case 'manager':
        return 'bg-orange-500/20 text-orange-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
  };

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div className='flex items-center justify-between'>
        <h1 className='text-2xl font-semibold text-slate-100 flex items-center gap-2'>
          <UserIcon className='w-6 h-6' />
          User Management
        </h1>
        <div className='flex items-center gap-3'>
          <Link to='/users/create'>
            <Button className='flex items-center gap-2'>
              <Plus className='w-4 h-4' />
              Create User
            </Button>
          </Link>
          <Link to='/admin-dashboard' className='text-blue-400 hover:text-blue-300 text-sm'>
            ← Back to Dashboard
          </Link>
        </div>
      </div>

      {/* Search and Filters */}
      <Card title='Search Users' subtitle='Find users by name, email, company, or role'>
        <div className='flex gap-3'>
          <div className='flex-1 relative'>
            <Search className='absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400' />
            <input
              type='text'
              placeholder='Search users by name, email, company, or role...'
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && handleSearch()}
              className='w-full pl-10 pr-4 py-2 bg-slate-700 border border-white/10 rounded-lg text-slate-100 focus:border-blue-500 focus:outline-none'
            />
          </div>
          <Button onClick={handleSearch} disabled={loading} variant='soft' className='px-6'>
            {loading ? 'Searching...' : 'Search'}
          </Button>
          <Button onClick={loadUsers} disabled={loading} variant='soft' className='px-6'>
            {loading ? 'Loading...' : 'Refresh'}
          </Button>
        </div>
        <p className='text-xs text-slate-400 mt-2'>
          Search is case sensitive - minimum 2 characters required
        </p>
      </Card>

      {/* Message Display */}
      {message && (
        <div
          className={`p-4 rounded-lg ${
            message.startsWith('✅')
              ? 'bg-emerald-500/20 text-emerald-400'
              : 'bg-rose-500/20 text-rose-400'
          }`}
        >
          {message}
        </div>
      )}

      {/* Users List */}
      <Card
        title={`Users (${filteredUsers.length})`}
        subtitle='Manage system users and their permissions'
      >
        {loading ? (
          <div className='text-center py-8 text-slate-400'>Loading users...</div>
        ) : filteredUsers.length === 0 ? (
          <div className='text-center py-8 text-slate-400'>
            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
          </div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-white/10'>
                  <th className='text-left p-3 text-slate-300 font-medium'>User</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Company</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Role</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Status</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Created</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Last Login</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr key={user.userId} className='border-b border-white/5 hover:bg-white/5'>
                    <td className='p-3'>
                      <div>
                        <div className='font-medium text-slate-200'>{user.username}</div>
                        <div className='text-sm text-slate-400 flex items-center gap-1'>
                          <Mail className='w-3 h-3' />
                          {user.email}
                        </div>
                        {user.phone && (
                          <div className='text-sm text-slate-400 flex items-center gap-1'>
                            <Phone className='w-3 h-3' />
                            {user.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className='p-3'>
                      {user.companyName ? (
                        <div className='flex items-center gap-1 text-slate-300'>
                          <Building2 className='w-3 h-3' />
                          {user.companyName}
                        </div>
                      ) : (
                        <span className='text-slate-500'>No company</span>
                      )}
                    </td>
                    <td className='p-3'>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleColor(user.role)}`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td className='p-3'>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(user.status)}`}
                      >
                        {user.status}
                      </span>
                    </td>
                    <td className='p-3 text-sm text-slate-400'>{formatDate(user.createdAt)}</td>
                    <td className='p-3 text-sm text-slate-400'>
                      {formatDate(user.lastLogin || '')}
                    </td>
                    <td className='p-3'>
                      <div className='flex items-center gap-2'>
                        <Link to={`/users/update/${user.userId}`}>
                          <Button variant='soft' className='px-2 py-1 text-xs'>
                            <Edit className='w-3 h-3' />
                          </Button>
                        </Link>
                        <Button
                          variant='soft'
                          className='px-2 py-1 text-xs text-blue-400 hover:bg-blue-500/20'
                          onClick={() => openRoleModal(user)}
                        >
                          <Shield className='w-3 h-3' />
                        </Button>
                        <Button
                          variant='soft'
                          className='px-2 py-1 text-xs text-rose-400 hover:bg-rose-500/20'
                          onClick={() => setDeleteModal({ show: true, user })}
                        >
                          <Trash2 className='w-3 h-3' />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Service Accounts Section */}
      {serviceAccounts.length > 0 && (
        <Card
          title={`Service Accounts (${serviceAccounts.length})`}
          subtitle='Company service accounts for SFTP access - managed through company settings'
        >
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead>
                <tr className='border-b border-white/10'>
                  <th className='text-left p-3 text-slate-300 font-medium'>Service Account</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Company</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Role</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Status</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Created</th>
                  <th className='text-left p-3 text-slate-300 font-medium'>Actions</th>
                </tr>
              </thead>
              <tbody>
                {serviceAccounts.map(user => (
                  <tr key={user.userId} className='border-b border-white/5 hover:bg-white/5'>
                    <td className='p-3'>
                      <div>
                        <div className='font-medium text-slate-200 flex items-center gap-2'>
                          <Server className='w-4 h-4 text-blue-400' />
                          {user.username}
                        </div>
                        <div className='text-sm text-slate-400 flex items-center gap-1'>
                          <Mail className='w-3 h-3' />
                          {user.email}
                        </div>
                      </div>
                    </td>
                    <td className='p-3'>
                      {user.companyName ? (
                        <div className='flex items-center gap-1 text-slate-300'>
                          <Building2 className='w-3 h-3' />
                          {user.companyName}
                        </div>
                      ) : (
                        <span className='text-slate-500'>No company</span>
                      )}
                    </td>
                    <td className='p-3'>
                      <span className='px-2 py-1 rounded-full text-xs font-medium bg-blue-600/20 text-blue-400'>
                        {user.role}
                      </span>
                    </td>
                    <td className='p-3'>
                      <span className='px-2 py-1 rounded-full text-xs font-medium bg-green-600/20 text-green-400'>
                        {user.status}
                      </span>
                    </td>
                    <td className='p-3 text-sm text-slate-400'>{formatDate(user.createdAt)}</td>
                    <td className='p-3'>
                      <div className='flex items-center gap-2'>
                        <Link to={`/companies`}>
                          <Button variant='soft' className='px-2 py-1 text-xs'>
                            <Building2 className='w-3 h-3' />
                            Manage
                          </Button>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal.show && deleteModal.user && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-slate-800 border border-white/10 rounded-lg p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-medium text-slate-100 mb-4'>Delete User</h3>
            <p className='text-slate-300 mb-6'>
              Are you sure you want to delete user <strong>{deleteModal.user.username}</strong>?
              This action cannot be undone.
            </p>
            <div className='flex gap-3'>
              <button
                onClick={closeDeleteModal}
                className='flex-1 px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteUser}
                disabled={loading}
                className='flex-1 px-4 py-2 bg-rose-600 text-white rounded hover:bg-rose-700 disabled:opacity-50 transition-colors'
              >
                {loading ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Role Update Modal */}
      {roleModal.show && roleModal.user && (
        <div className='fixed inset-0 bg-black/50 flex items-center justify-center z-50'>
          <div className='bg-slate-800 border border-white/10 rounded-lg p-6 max-w-md w-full mx-4'>
            <h3 className='text-lg font-medium text-slate-100 mb-4'>Update User Role</h3>
            <p className='text-slate-300 mb-4'>
              Update role for user <strong>{roleModal.user.username}</strong>
            </p>
            <div className='mb-6'>
              <label className='block text-sm font-medium text-slate-300 mb-2'>
                Select New Role
              </label>
              <select
                value={roleModal.newRole}
                onChange={e => setRoleModal({ ...roleModal, newRole: e.target.value })}
                className='w-full px-3 py-2 bg-slate-700 border border-white/10 rounded-lg text-slate-100 focus:border-blue-500 focus:outline-none'
              >
                <option value='standard-user'>Standard User</option>
                <option value='admin'>Admin</option>
                <option value='super-user'>Super User</option>
              </select>
              <div className='mt-2 text-xs text-slate-400'>
                <div>
                  <strong>Standard User:</strong> Can only access their own files and company
                  folders
                </div>
                <div>
                  <strong>Admin:</strong> Can access all company folders and manage users
                </div>
                <div>
                  <strong>Super User:</strong> Can access all folders and perform all operations
                </div>
              </div>
            </div>
            <div className='flex gap-3'>
              <button
                onClick={closeRoleModal}
                className='flex-1 px-4 py-2 border border-white/10 rounded text-slate-200 hover:bg-white/10 transition-colors'
              >
                Cancel
              </button>
              <button
                onClick={handleRoleUpdate}
                disabled={loading || roleModal.newRole === roleModal.user.role}
                className='flex-1 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors'
              >
                {loading ? 'Updating...' : 'Update Role'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copyright with Easter egg */}
      <Copyright className='mt-8' showEasterEgg={true} />
    </div>
  );
}

/*
 * OHIT Watermark - Optimum Healthcare IT Transfer System
 * Copyright © 2025 Optimum Healthcare IT. All rights reserved.
 *
 * This file is part of the OHIT Admin Portal for secure healthcare file transfer.
 * Built with React, TypeScript, and AWS CDK for HIPAA-compliant operations.
 *
 * OHIT: Optimized for Health Information Technology
 * Secure, reliable, and compliant file transfer solutions for healthcare organizations.
 */
